import { Graphics, InteractionEvent, Loader, VideoResource } from 'pixi.js';
import { FRAME_THEME } from '../constant';
import { Game, Scene, Store } from '../core';
import { StarCutAway } from '../cutaway';
import { Point } from '../interface';
import { Button } from '../ui';
import { JumpingGameScene } from '.';
import { Cut } from '../class/opening/index';
import { sound } from '@pixi/sound';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';

class ThirdCutScene extends Scene {
  private cameraPosition: Point;
  private cutScenes: Array<Cut>;
  private currentCutScene?: Cut;
  private disabled: boolean;

  constructor(game: Game) {
    super(game);
    this.cutScenes = [];
    this.cameraPosition = { x: 540, y: 960 };
    this.disabled = false;
    this.createBackground();
    this.createCutScenes();
    this.createSkipButton();

    this.camera.positionChasingCoefficient = 0.1;
    this.camera.setOffset({ x: 0, y: 0 });
    this.camera.setTargetPosition(this.cameraPosition, true);
    logging(LOG_EVENT.GAME_START, LOG_SCENE.BRIDGE_SCENE_2, Store.instance.data.playername, window.navigator.userAgent);
  }

  private createBackground(): void {
    const background = new Graphics();
    background.beginFill(0x000000);
    background.drawRect(0, 0, 5400, 1920);
    background.endFill();

    this.stage.addChild(background);
  }

  private createCutScenes(): void {
    const base = Store.instance.state.locale === 'en-US' ? 'en' : 'ko';
    const cutSpriteInfoArrays: Array<
      Array<{
        audio?: string;
        texture: {
          type: 'IMAGE' | 'VIDEO';
          src: string;
        };
      }>
    > = [
      [
        {
          texture: { type: 'IMAGE', src: `/textures/cut/${base}/cut_3_1.jpeg` },
        },
      ],
      [
        {
          texture: { type: 'IMAGE', src: `/textures/cut/${base}/cut_3_2.jpeg` },
        },
      ],
    ];

    for (let i = 0; i < cutSpriteInfoArrays.length; i++) {
      const cutSpriteInfoArray: Array<{
        audio?: string;
        texture: {
          type: 'IMAGE' | 'VIDEO';
          src: string;
        };
      }> = cutSpriteInfoArrays[i];
      const cut = new Cut(cutSpriteInfoArray, 1);

      cut.x = 540 + (i - 1) * 1080;
      cut.y = 960;

      this.cutScenes.push(cut);
    }

    for (const cutScene of this.cutScenes) {
      cutScene.on('sceneFinished', () => {
        const index = this.cutScenes.indexOf(cutScene);
        if (index < this.cutScenes.length - 1) {
          this.setCutScene(this.cutScenes[index + 1]);
        } else {
          this.currentCutScene = undefined;
          this.changeScene();
        }
      });
      this.stage.addChild(cutScene);
    }

    this.setCutScene(this.cutScenes[0]);
  }

  protected pointerdown = (_event: InteractionEvent): void => {
    if (
      this.currentCutScene &&
      Math.abs(this.camera.position.x - this.currentCutScene.x) < 10 &&
      Math.abs(this.camera.position.y - this.currentCutScene.y) < 10
    ) {
      this.currentCutScene.next();
    }
  };

  private setCutScene(cutScene: Cut): void {
    this.cameraPosition.x = cutScene.x;
    this.cameraPosition.y = cutScene.y;
    this.currentCutScene = cutScene;
  }

  private createSkipButton(): void {
    const button = new Button({
      theme: FRAME_THEME.BLACK,
      fontSize: 16,
      fill: 0xffffff,
      width: 70,
      height: 13,
      fontFamily: 'PressStart2P',
      margin: {
        left: 8,
        right: 6,
        top: 4,
        bottom: 1,
      },
    });
    button.setText('SKIP');
    button.position.x = 20;
    button.position.y = this.game.height - button.options.height - 12;
    button.on('pointerdown', (e: InteractionEvent) => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      e.stopPropagation();
    });
    button.on('pointerup', this.changeScene);

    this.uiContainer.addChild(button);
  }

  public changeScene = (): void => {
    const cutaway = new StarCutAway();

    cutaway.setSize(this.game.width, this.game.height);

    this.game.cutaway = cutaway;
    this.game.changeScene(JumpingGameScene);
    this.disabled = true;
    for (const cutScene of this.cutScenes) {
      for (const cutSprite of cutScene.cutSprites) {
        if (cutSprite.type === 'VIDEO') {
          (cutSprite.sprite.texture.baseTexture.resource as VideoResource).source.pause();
        }
      }
    }
  };

  public update(elapsed: number): void {
    super.update(elapsed);
    // Target Cut Update & if End? -> Next Cut Position & Target Change...

    if (!this.disabled) {
      if (this.currentCutScene) {
        this.currentCutScene.update(elapsed);
      }
    }
  }

  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {
    const base = Store.instance.state.locale === 'en-US' ? 'en' : 'ko';
    const assets: Array<string> = [
      `/textures/cut/${base}/cut_3_1.jpeg`,
      `/textures/cut/${base}/cut_3_2.jpeg`,
      '/textures/ui/frame/black.png',
    ];

    let maxLoaded = 1;
    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
        maxLoaded++;
      }
    }

    let loaded: number = 1;
    return new Promise((resolve) => {
      Loader.shared.onLoad.add(() => {
        callback(++loaded, maxLoaded);
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
    });
  };

  public destroy(): void {
    super.destroy();

    for (const cutScene of this.cutScenes) {
      cutScene.destroy();
    }
  }
}

export default ThirdCutScene;
